import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarHeart, Plus, Trash2, Gift, Cake, Heart, Sparkles } from "lucide-react";
import { api, getGuestId } from "@/lib/api";
import { useGet } from "@/lib/hooks";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const OCCASIONS = ["Birthday", "Anniversary", "Rakhi", "Friendship Day", "Just Because"];
const OCC_ICON = { Birthday: Cake, Anniversary: Heart, Rakhi: Gift, "Friendship Day": Sparkles, "Just Because": Gift };

function daysUntil(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  const next = new Date(today.getFullYear(), d.getMonth(), d.getDate());
  if (next < today) next.setFullYear(today.getFullYear() + 1);
  return Math.round((next - today) / 86400000);
}

export default function Reminders() {
  const guestId = getGuestId();
  const [refresh, setRefresh] = useState(0);
  const { data: reminders } = useGet(`/reminders?guest_id=${guestId}`, [guestId, refresh]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", person: "", occasion: "Birthday", date: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title || !form.date) return toast.error("Please add a title and date.");
    setSaving(true);
    try {
      await api.post("/reminders", { ...form, guest_id: guestId });
      toast.success("Reminder saved 💝");
      setForm({ title: "", person: "", occasion: "Birthday", date: "", notes: "" });
      setOpen(false); setRefresh((r) => r + 1);
    } catch { toast.error("Could not save reminder."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/reminders/${id}`); setRefresh((r) => r + 1); toast.success("Reminder removed"); }
    catch { toast.error("Could not remove."); }
  };

  const list = reminders || [];

  return (
    <div className="wp-container py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionHeading eyebrow="Never miss a moment" title="Occasion Reminders" subtitle="Save birthdays, anniversaries &amp; Rakhi — we’ll keep them handy so you’re always gift-ready." />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="occasion-add-reminder-button" className="rounded-xl bg-wp-rose-strong text-white"><Plus className="mr-2 h-4 w-4" /> Add reminder</Button>
          </DialogTrigger>
          <DialogContent className="rounded-wp-xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">Add a reminder</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Title (e.g. Mom’s Birthday)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="reminder-title-input" className="rounded-wp-md" />
              <Input placeholder="Person (optional)" value={form.person} onChange={(e) => setForm({ ...form, person: e.target.value })} className="rounded-wp-md" />
              <Select value={form.occasion} onValueChange={(v) => setForm({ ...form, occasion: v })}>
                <SelectTrigger data-testid="reminder-occasion-select" className="rounded-wp-md"><SelectValue /></SelectTrigger>
                <SelectContent>{OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="reminder-date-input" className="rounded-wp-md" />
              <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-wp-md" />
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="reminder-save-button" className="w-full rounded-xl bg-wp-rose-strong text-white">{saving ? "Saving…" : "Save reminder"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="mt-12 flex flex-col items-center rounded-wp-xl border border-dashed border-border bg-card py-16 text-center shadow-wp-soft">
          <div className="grid h-20 w-20 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong"><CalendarHeart className="h-9 w-9" /></div>
          <p className="mt-4 font-display text-xl">No reminders yet</p>
          <p className="mt-1 max-w-sm text-sm text-wp-ink-muted">Add the special dates you care about and we’ll help you gift on time, every time.</p>
          <Button onClick={() => setOpen(true)} className="mt-5 rounded-xl bg-wp-rose-strong text-white"><Plus className="mr-2 h-4 w-4" /> Add your first reminder</Button>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {list.map((r) => {
              const Icon = OCC_ICON[r.occasion] || Gift;
              const days = daysUntil(r.date);
              return (
                <motion.div key={r.id} layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                  className="rounded-wp-lg border border-border bg-card p-5 shadow-wp-card" data-testid="occasion-reminder-card">
                  <div className="flex items-start justify-between">
                    <span className="grid h-11 w-11 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong"><Icon className="h-5 w-5" /></span>
                    <button onClick={() => remove(r.id)} aria-label="Delete" data-testid={`reminder-delete-${r.id}`} className="text-wp-ink-muted hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <h3 className="mt-3 font-display text-lg">{r.title}</h3>
                  <p className="text-sm text-wp-ink-muted">{r.occasion}{r.person ? ` · ${r.person}` : ""}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="wp-chip">{days === 0 ? "Today!" : `in ${days} day${days > 1 ? "s" : ""}`}</span>
                    <Link to="/shop" className="text-sm font-medium text-wp-rose-strong hover:underline">Find a gift</Link>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
